// Drives the reload once serviceWorkerRegistration.ts's onUpdate fires. CRA's default
// template only ever logs when an update installs, then leaves it in `waiting` until
// every tab is closed — for an app people leave open, that's effectively "never". This
// posts SKIP_WAITING (service-worker.ts already listens for it and calls
// self.skipWaiting()) and reloads once the new worker takes control, the same approach
// ~/vine-web's src/lib/pwaUpdate.ts uses via vite-plugin-pwa's registerSW(), adapted here
// since there's no virtual:pwa-register — this is plain CRA/Workbox InjectManifest.
import i18n from '../i18n';

const RELOAD_DELAY_MS = 1500;
const DIRTY_RECHECK_MS = 3000;
const ACTIVATION_TIMEOUT_MS = 10000;
const RELOAD_LOOP_WINDOW_MS = 60000;
// Two deploys landing minutes apart legitimately auto-update twice in a row, so a single repeat
// is not evidence of a loop. A third reload inside the window is: no real release sequence needs
// it, and a genuine loop keeps producing them.
const MAX_CONSECUTIVE_AUTO_RELOADS = 2;
// A worker from before GET_BUILD_ID existed never answers; don't hold the update up for long.
const BUILD_ID_TIMEOUT_MS = 1000;
const BANNER_ID = 'pwa-update-banner';
const AUTO_RELOAD_KEY = 'pwaUpdateAutoReloads';
// How often a long-open app asks the server for a newer service worker.
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

// Signed in = a non-empty access_token (sign-out stores ''); see SharedCommon/utils.js.
// Storage can throw when site data is blocked — then nobody can be signed in either.
function isSignedIn(): boolean {
  try {
    return !!localStorage.getItem('access_token');
  } catch (error) {
    return false;
  }
}

// Crude but broad "is the user mid-typing" signal: a focused, non-empty text
// input/textarea/contenteditable. There's no app-wide dirty-form registry to check
// instead. Not perfect (a filled field that lost focus won't count, a tab left open with
// a populated field focused indefinitely defers forever) but good enough to stop the
// update from yanking out from under someone actively typing.
//
// Only while signed in. Signed out, the only reachable screens are sign-in / sign-up /
// new-password: nothing costly to retype (keychain autofill), and stale code there is
// exactly what locks people out — the old build keeps failing while the update waits for
// the focused email/phone field to empty, which on a login form it never does.
function hasUnsavedInput(): boolean {
  if (!isSignedIn()) {
    return false;
  }
  const el = document.activeElement;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) {
    return el.value.trim().length > 0;
  }
  if (el instanceof HTMLElement && el.isContentEditable) {
    return (el.textContent ?? '').trim().length > 0;
  }
  return false;
}

let reloading = false;
let controllerChangeBound = false;
// The build this page was loaded from, asked of its controlling worker at startup.
let pageBuildId: Promise<string | undefined> | undefined;

// Asks a worker which build it serves (service-worker.ts answers GET_BUILD_ID). Undefined when it
// can't say: no worker, or one from before the message existed.
function requestBuildId(worker: ServiceWorker | null): Promise<string | undefined> {
  if (!worker || typeof MessageChannel === 'undefined') {
    return Promise.resolve(undefined);
  }
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => resolve(undefined), BUILD_ID_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent) => {
      window.clearTimeout(timer);
      resolve(typeof event.data?.buildId === 'string' ? event.data.buildId : undefined);
    };
    try {
      worker.postMessage({ type: 'GET_BUILD_ID' }, [channel.port2]);
    } catch (error) {
      window.clearTimeout(timer);
      resolve(undefined);
    }
  });
}

// Plain DOM, not a Fluent component: this can fire before React has mounted (an update
// already waiting from a prior session, checked right after registration) or from
// outside the component tree entirely, same reason index.tsx/Notify.tsx use styled divs.
function showUpdateBanner(message: string): void {
  // A reload is already queued but does not stop this script synchronously, so a later timer
  // could otherwise re-create the banner we just removed and strand it on the page.
  if (reloading) {
    return;
  }
  let banner = document.getElementById(BANNER_ID);
  if (!banner) {
    banner = document.createElement('div');
    banner.id = BANNER_ID;
    banner.style.cssText =
      'position:fixed;left:0;right:0;bottom:0;z-index:9999;padding:12px 16px;' +
      'background:#323130;color:#fff;font-size:14px;text-align:center;';
    document.body.appendChild(banner);
  }
  banner.textContent = message;
}

function removeUpdateBanner(): void {
  document.getElementById(BANNER_ID)?.remove();
}

function reloadOnceControllerChanges(): void {
  if (controllerChangeBound) {
    return;
  }
  controllerChangeBound = true;
  navigator.serviceWorker.addEventListener('controllerchange', reloadForNewController);
}

function reloadForNewController(): void {
  if (reloading) {
    return;
  }
  // Counted here, at the reload itself, not when SKIP_WAITING is posted: the controller can
  // change before that timer ever runs (DevTools' "Update on reload" activates every new
  // worker immediately), and those uncounted reloads let the loop guard never trip.
  if (isReloadLooping()) {
    showManualUpdateBanner();
    return;
  }
  reloading = true;
  markAutoReload();
  removeUpdateBanner();
  window.location.reload();
}

interface IAutoReloadStreak {
  count: number;
  at: number;
}

// sessionStorage, not a module variable: the whole point is to carry what we already did across
// the reload itself. Can throw when site data is blocked, so every access is guarded.
function readAutoReloadStreak(): IAutoReloadStreak {
  try {
    const stored = JSON.parse(sessionStorage.getItem(AUTO_RELOAD_KEY) ?? '');
    return { count: Number(stored?.count) || 0, at: Number(stored?.at) || 0 };
  } catch (error) {
    return { count: 0, at: 0 };
  }
}

function markAutoReload(): void {
  const previous = readAutoReloadStreak();
  const now = Date.now();
  const streak = now - previous.at < RELOAD_LOOP_WINDOW_MS ? previous.count : 0;
  try {
    sessionStorage.setItem(AUTO_RELOAD_KEY, JSON.stringify({ count: streak + 1, at: now }));
  } catch (error) {
    // Site data blocked: the loop guard degrades to off, which is the pre-existing behaviour.
  }
}

function isReloadLooping(): boolean {
  const streak = readAutoReloadStreak();
  return streak.count >= MAX_CONSECUTIVE_AUTO_RELOADS && Date.now() - streak.at < RELOAD_LOOP_WINDOW_MS;
}

function showManualUpdateBanner(): void {
  showUpdateBanner(i18n.t('Pwa.update.manual', 'New version available. Refresh to get the update.'));
}

export function handleServiceWorkerUpdate(registration: ServiceWorkerRegistration): void {
  const waiting = registration.waiting;
  if (!waiting || reloading) {
    return;
  }
  if (!pageBuildId) {
    applyUpdate(waiting);
    return;
  }

  // A new worker does not always mean a new release: DevTools' "Update on reload" reinstalls the
  // same build on every load. Announcing (and reloading for) that would claim a version that
  // doesn't exist, so only go ahead when the builds differ or either side can't tell.
  void Promise.all([pageBuildId, requestBuildId(waiting)]).then(([current, next]) => {
    if (current && current === next) {
      // Same app files, but the worker itself may still differ: the id covers only the precache,
      // which never includes service-worker.js. Let it take over quietly so a worker-only change
      // still applies. No reload is needed for files that didn't change, and none happens: the
      // controllerchange listener is only bound by applyUpdate.
      waiting.postMessage({ type: 'SKIP_WAITING' });
      return;
    }
    // The controllerchange listener is only bound by applyUpdate, after this comparison. If another
    // tab activated the new worker meanwhile (its own SKIP_WAITING), that event has already passed
    // and SKIP_WAITING would do nothing more: the new build controls this page already, so reload now.
    if (navigator.serviceWorker.controller === waiting) {
      reloadForNewController();
      return;
    }
    applyUpdate(waiting);
  });
}

function applyUpdate(waiting: ServiceWorker): void {
  if (reloading) {
    return;
  }

  // Reloading repeatedly without ever clearing the update means the reload is not settling it:
  // either skipWaiting never took effect, or a fresh worker installs on every load (what
  // DevTools' "Update on reload" does). Reloading again would refresh forever, so ask instead.
  if (isReloadLooping()) {
    showManualUpdateBanner();
    return;
  }

  reloadOnceControllerChanges();

  if (hasUnsavedInput()) {
    showManualUpdateBanner();
  } else {
    showUpdateBanner(i18n.t('Pwa.update.auto', 'New version available, will auto reload…'));
  }

  const attemptReload = () => {
    if (hasUnsavedInput()) {
      window.setTimeout(attemptReload, DIRTY_RECHECK_MS);
      return;
    }
    waiting.postMessage({ type: 'SKIP_WAITING' });
    // Some browsers (notably embedded WebViews) never activate the waiting worker, leaving the
    // "will auto reload" promise on screen forever. Correct the banner instead of lying.
    window.setTimeout(() => {
      if (!reloading) {
        showManualUpdateBanner();
      }
    }, ACTIVATION_TIMEOUT_MS);
  };
  window.setTimeout(attemptReload, RELOAD_DELAY_MS);
}

// The browser only looks for a new service worker on a full page load. An installed app
// is usually *resumed* from the background instead (always, on iOS home-screen apps) — no
// load, no check — so it could run an old build indefinitely. Ask again whenever the app
// returns to the foreground, and hourly while it stays open. A newer worker found this way
// goes through serviceWorkerRegistration.ts's onupdatefound → handleServiceWorkerUpdate.
export function installServiceWorkerUpdateChecks(): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  // Ask now, before a newer worker can take over: the controller at startup is the build this page
  // was loaded from.
  pageBuildId = requestBuildId(navigator.serviceWorker.controller);
  navigator.serviceWorker.ready
    .then((registration) => {
      const check = () => {
        if (navigator.onLine) {
          registration.update().catch(() => {
            // Offline or the server hiccupped: the next foreground / interval tries again.
          });
        }
      };
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          check();
        }
      });
      window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    })
    .catch(() => {
      // No service worker became ready (unsupported/blocked): nothing to keep updated.
    });
}
